export const INTERNAL_NETWORK_C2_AGENT_MESSAGE_PATH =
  '/orchestrator/internal-networks/c2/events';

export const INTERNAL_NETWORK_C2_AGENT_MESSAGE_LEGACY_PATH =
  '/orchestrator/internal-networks/c2/agent-message';

export const INTERNAL_NETWORK_REMOTE_C2_ADMIN_NOT_CONFIGURED_MESSAGE =
  'Remote C2 admin credentials are not configured';

export const buildInternalNetworkRemoteC2AdminNotConfiguredEnrollmentError =
  (): string =>
    `${INTERNAL_NETWORK_REMOTE_C2_ADMIN_NOT_CONFIGURED_MESSAGE}; cannot provision sensor payload UUID`;

export const ALLOWED_REMOTE_EXECUTION_COMMANDS = [
  'discovery_collect',
  'bloodhound_collect',
  'credential_verify',
  'kerberoast_request',
  'asrep_roast',
  'acl_probe',
  'acl_abuse',
  'pass_the_hash',
  'dcsync',
  'delegation_abuse',
  'adcs_probe',
  'certipy',
  'lateral_move_probe',
  'laps_read',
  'gmsa_read',
  'psremote_probe',
  'ntlm_coerce',
  'spawn_agent',
  'link_agent',
  'unlink_agent',
  'deploy_payload',
  'ssh_auth',
  'socks_start',
  'socks_stop',
  'inject_agent',
  'persist_agent',
  'cleanup_agent',
  'hash_crack_offline',
] as const;

export type AllowedRemoteExecutionCommand =
  (typeof ALLOWED_REMOTE_EXECUTION_COMMANDS)[number];

export const POSEIDON_ONLY_REMOTE_EXECUTION_COMMANDS = [
  'spawn_agent',
  'deploy_payload',
  'ssh_auth',
  'socks_start',
  'socks_stop',
] as const satisfies readonly AllowedRemoteExecutionCommand[];

export type PoseidonOnlyRemoteExecutionCommand =
  (typeof POSEIDON_ONLY_REMOTE_EXECUTION_COMMANDS)[number];

export const HOST_ONLY_REMOTE_EXECUTION_COMMANDS = [
  'pass_the_hash',
  'lateral_move_probe',
  'psremote_probe',
] as const satisfies readonly AllowedRemoteExecutionCommand[];

export const isPoseidonOnlyRemoteExecutionCommand = (
  command: string,
): command is PoseidonOnlyRemoteExecutionCommand =>
  POSEIDON_ONLY_REMOTE_EXECUTION_COMMANDS.some(
    (allowed) => allowed === command,
  );

export const TERRA_ENGINE_UNSUPPORTED_REMOTE_EXECUTION_COMMANDS = [
  ...POSEIDON_ONLY_REMOTE_EXECUTION_COMMANDS,
  'persist_agent',
  'inject_agent',
  'cleanup_agent',
] as const satisfies readonly AllowedRemoteExecutionCommand[];

export const isTerraEngineUnsupportedRemoteExecutionCommand = (
  command: string,
): boolean =>
  TERRA_ENGINE_UNSUPPORTED_REMOTE_EXECUTION_COMMANDS.some(
    (unsupported) => unsupported === command,
  );

export const OFFENSIVE_REMOTE_EXECUTION_COMMANDS_REQUIRING_SCENARIO = [
  'kerberoast_request',
  'asrep_roast',
  'acl_probe',
  'acl_abuse',
  'pass_the_hash',
  'dcsync',
  'delegation_abuse',
  'adcs_probe',
  'certipy',
  'lateral_move_probe',
  'laps_read',
  'gmsa_read',
  'psremote_probe',
  'ntlm_coerce',
] as const satisfies readonly AllowedRemoteExecutionCommand[];

export type OffensiveRemoteExecutionCommandRequiringScenario =
  (typeof OFFENSIVE_REMOTE_EXECUTION_COMMANDS_REQUIRING_SCENARIO)[number];

export const isOffensiveRemoteExecutionCommandRequiringScenario = (
  command: string,
): command is OffensiveRemoteExecutionCommandRequiringScenario =>
  OFFENSIVE_REMOTE_EXECUTION_COMMANDS_REQUIRING_SCENARIO.some(
    (offensive) => offensive === command,
  );

export const TERRA_ENGINE_OPERATOR_COMMANDS = [
  'discovery_collect',
  'bloodhound_collect',
  'credential_verify',
  'kerberoast_request',
  'asrep_roast',
  'acl_probe',
  'acl_abuse',
  'pass_the_hash',
  'dcsync',
  'delegation_abuse',
  'adcs_probe',
  'certipy',
  'lateral_move_probe',
  'laps_read',
  'gmsa_read',
  'psremote_probe',
  'ntlm_coerce',
  'link_agent',
  'unlink_agent',
  'hash_crack_offline',
] as const satisfies readonly AllowedRemoteExecutionCommand[];

export type TerraEngineOperatorCommand =
  (typeof TERRA_ENGINE_OPERATOR_COMMANDS)[number];

export const TERRA_ENGINE_UNSUPPORTED_COMMAND_MESSAGE =
  'Command is not supported on the container engine callback';

export const CERTIPY_ACTIONS = ['find', 'req', 'auth'] as const;

export type CertipyAction = (typeof CERTIPY_ACTIONS)[number];

export const CERTIPY_INVALID_ACTION_MESSAGE =
  'certipy action must be one of find|req|auth';

export const UNLINK_AGENT_MISSING_PEER_ID_MESSAGE =
  'unlink_agent requires peer_id';

export const NOTABLE_REMOTE_EXECUTION_COMMANDS = [
  'dcsync',
  'kerberoast_request',
  'asrep_roast',
  'persist_agent',
  'hash_crack_offline',
  'lateral_move_probe',
] as const satisfies readonly AllowedRemoteExecutionCommand[];

export type NotableRemoteExecutionCommand =
  (typeof NOTABLE_REMOTE_EXECUTION_COMMANDS)[number];

export const isAllowedRemoteExecutionCommand = (
  command: string,
): command is AllowedRemoteExecutionCommand =>
  ALLOWED_REMOTE_EXECUTION_COMMANDS.some((allowed) => allowed === command);
